import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Check, ChevronRight, MapPin, User, ListTodo, Building2, Bot, X, Sparkles } from 'lucide-react';

const stepIcons = {
  welcome: Sparkles, profile: User, location: MapPin,
  first_task: ListTodo, first_customer: Building2, ai_assistant: Bot, complete: Check,
};

export function OnboardingWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [progress, setProgress] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const fetchOnboarding = async () => {
      try {
        const res = await api.get('/onboarding');
        setSteps(res.data.steps);
        setProgress(res.data.progress);
        if (!res.data.progress.is_complete) setVisible(true);
      } catch { /* silent */ }
    };
    fetchOnboarding();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const completeStep = async (stepId) => {
    try {
      const res = await api.post('/onboarding/complete-step', { step_id: stepId });
      setProgress(prev => ({
        ...prev,
        completed_steps: res.data.completed_steps,
        current_step: res.data.current_step,
        is_complete: res.data.is_complete,
      }));
      if (res.data.is_complete) setTimeout(() => setVisible(false), 1500);
    } catch { /* silent */ }
  };

  const skipOnboarding = async () => {
    try {
      await api.post('/onboarding/skip');
      setVisible(false);
    } catch { /* silent */ }
  };

  const handleStepAction = (step) => {
    completeStep(step.id);
    if (step.link) navigate(step.link);
    if (step.id === 'location' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(() => {}, () => {}, { enableHighAccuracy: true });
    }
  };

  if (!visible || !progress || progress.is_complete) return null;

  const completedSet = new Set(progress.completed_steps || []);
  const completionPct = Math.round((completedSet.size / (steps.length - 1)) * 100);

  return (
    <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-6" data-testid="onboarding-wizard">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Outfit' }}>
              Welcome to AIVORA Nexus, {user?.name?.split(' ')[0]}!
            </h3>
            <p className="text-sm text-gray-500 mt-1">Complete these steps to get started</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-600">{completionPct}%</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={skipOnboarding} data-testid="skip-onboarding">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-blue-100 rounded-full mb-4 overflow-hidden">
          <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${completionPct}%` }} />
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {steps.filter(s => s.id !== 'complete').map((step) => {
            const Icon = stepIcons[step.id] || Check;
            const done = completedSet.has(step.id);
            const isCurrent = progress.current_step === step.id;
            return (
              <button key={step.id}
                onClick={() => !done && handleStepAction(step)}
                disabled={done}
                className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                  done ? 'bg-green-50 border border-green-200'
                    : isCurrent ? 'bg-white border-2 border-blue-400 shadow-sm'
                    : 'bg-white/60 border border-gray-200 hover:bg-white hover:shadow-sm'
                }`}
                data-testid={`onboarding-step-${step.id}`}
              >
                <div className={`p-2 rounded-lg flex-shrink-0 ${done ? 'bg-green-100 text-green-600' : isCurrent ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                  {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${done ? 'text-green-700 line-through' : 'text-gray-900'}`}>{step.title}</p>
                  <p className="text-xs text-gray-500 truncate">{step.description}</p>
                </div>
                {!done && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
